import React, {useState, useEffect} from 'react';
import {
    Box,
    Button,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Fab,
    Grid,
    IconButton,
    Paper,
    Popover,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    withStyles
} from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import {Add, KeyboardArrowDown, KeyboardArrowUp, Edit, Delete} from "@material-ui/icons";
import {SketchPicker} from 'react-color';
import fb from "firebase/app";
import {appStyles} from "../../styles";
import FlightOptionsContext from "../Session/flightOptionsContext";
import {withFirebase} from "../Firebase";
import ConfirmDialog from "../Forms/ConfirmDialog";
import EventTypeSelector from "../Forms/EventTypeSelector";

const ColorPicker = withStyles(appStyles)(({ color, onChangeComplete, classes }) => {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    return (
        <div>
            <div
                className={classes.colorPickerSwatch}
                onClick={handleClick}
            >
                <div
                    className={classes.colorPickerColor}
                    style={{background: color}}
                />
            </div>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <SketchPicker color={color} onChangeComplete={(color) => onChangeComplete(color.hex)} />
            </Popover>
        </div>
    );
});


export const SortieDefaultForm = (props) => {
    const {open, handleClose, data, firebase, isAdding} = props;
    const [id, setId] = useState(data.id || '');
    const [name, setName] = useState(data.name);
    const [takeoffTime, setTakeoffTime] = useState(data.takeoffTime);
    const [landTime, setLandTime] = useState(data.landTime);
    const [eShow, setEShow] = useState(data.eShow);
    const [oShow, setOShow] = useState(data.oShow);
    const [cr, setCr] = useState(data.cr);
    const [bgColor, setBgColor] = useState(data.bgColor);
    const [textColor, setTextColor] = useState(data.textColor);
    const [errors, setErrors] = useState({});

    const validate = () => {
        let newErrors = {};
        const t = parseFloat(takeoffTime);
        const l = parseFloat(landTime);
        const e = parseFloat(eShow);
        const o = parseFloat(oShow);
        const c = parseFloat(cr);

        if (isNaN(t) || t < 0 || t >= 24) newErrors.takeoffTime = "Must be between 0 and 23.99";
        if (isNaN(l) || l <= t) newErrors.landTime = "Must be greater than Takeoff Time";
        if (isNaN(e) || e > 10) newErrors.eShow = "Max 10 hours before takeoff";
        if (isNaN(o) || o > 10) newErrors.oShow = "Max 10 hours before takeoff";
        if (isNaN(c) || c > 5) newErrors.cr = "Max 5 hours before takeoff";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (!validate()) return;

        const updatedData = {
            name,
            takeoffTime: parseFloat(takeoffTime),
            landTime: parseFloat(landTime),
            eShow: parseFloat(eShow),
            oShow: parseFloat(oShow),
            cr: parseFloat(cr),
            bgColor,
            textColor,
        };
        if (isAdding) {
            firebase.sortieDefault(id).set(updatedData).then(() => handleClose());
        } else {
            firebase.sortieDefault(data.id).set(updatedData).then(() => handleClose());
        }
    };

    return (
        <Dialog open={open} onClose={handleClose}>
            <DialogTitle>{isAdding ? 'Add Sortie Default' : 'Edit Sortie Default'}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    {isAdding && <Grid item xs={12}>
                        <TextField id="sortie-type" label="Type" value={id} onChange={(e) => setId(e.target.value)} fullWidth/>
                    </Grid>}
                    <Grid item xs={12}>
                        <TextField id="sortie-name" label="Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            id="sortie-takeoff"
                            label="Takeoff Time"
                            value={takeoffTime}
                            onChange={(e) => setTakeoffTime(e.target.value)}
                            fullWidth
                            type="number"
                            inputProps={{ step: "0.25" }}
                            error={!!errors.takeoffTime}
                            helperText={errors.takeoffTime || "Hours from start of day (0-23.99)"}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            id="sortie-land"
                            label="Land Time"
                            value={landTime}
                            onChange={(e) => setLandTime(e.target.value)}
                            fullWidth
                            type="number"
                            inputProps={{ step: "0.25" }}
                            error={!!errors.landTime}
                            helperText={errors.landTime || "Hours from start of day (> Takeoff)"}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            id="sortie-eshow"
                            label="E Show"
                            value={eShow}
                            onChange={(e) => setEShow(e.target.value)}
                            fullWidth
                            type="number"
                            inputProps={{ step: "0.25" }}
                            error={!!errors.eShow}
                            helperText={errors.eShow || "Hours before takeoff (max 10)"}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            id="sortie-oshow"
                            label="O Show"
                            value={oShow}
                            onChange={(e) => setOShow(e.target.value)}
                            fullWidth
                            type="number"
                            inputProps={{ step: "0.25" }}
                            error={!!errors.oShow}
                            helperText={errors.oShow || "Hours before takeoff (max 10)"}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            id="sortie-cr"
                            label="Crew Ready"
                            value={cr}
                            onChange={(e) => setCr(e.target.value)}
                            fullWidth
                            type="number"
                            inputProps={{ step: "0.25" }}
                            error={!!errors.cr}
                            helperText={errors.cr || "Hours before takeoff (max 5)"}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography>BG Color</Typography>
                        <ColorPicker color={bgColor} onChangeComplete={setBgColor}/>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography>Text Color</Typography>
                        <ColorPicker color={textColor} onChangeComplete={setTextColor}/>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleSave} color="primary">Save</Button>
            </DialogActions>
        </Dialog>
    );
}

const DefaultEventForm = (props) => {
    const {open, handleClose, eventData, sortieDefaultId, firebase, isAdding} = props;
    const flightOptions = React.useContext(FlightOptionsContext);
    const [type, setType] = useState(eventData ? eventData.type : (flightOptions.sortieEvents ? Object.keys(flightOptions.sortieEvents)[0] : ''));
    const [time, setTime] = useState(eventData ? eventData.time : 0);
    const [length, setLength] = useState(eventData ? eventData.length : 0);
    const [location, setLocation] = useState(eventData ? eventData.location : '');

    useEffect(() => {
        if (eventData) {
            setType(eventData.type);
            setTime(eventData.time);
            setLength(eventData.length);
            setLocation(eventData.location);
        } else {
            setType(flightOptions.sortieEvents ? Object.keys(flightOptions.sortieEvents)[0] : '');
            setTime(0);
            setLength(0);
            setLocation('');
        }
    }, [eventData, flightOptions.sortieEvents]);

    const handleSave = () => {
        const eventId = isAdding ? type : eventData.id;
        const updatedEvent = { type, time, length, location };
        firebase.sortieDefault(sortieDefaultId).update({
            [`events.${eventId}`]: updatedEvent
        }).then(() => handleClose());
    };

    return (
        <Dialog open={open} onClose={handleClose}>
            <DialogTitle>{isAdding ? 'Add Default Event' : 'Edit Default Event'}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <EventTypeSelector value={type} onChange={setType} sortieEvents={flightOptions.sortieEvents} disabled={!isAdding}/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Time Offset (hours from T/O)"
                            type="number"
                            value={time}
                            onChange={(e) => setTime(parseFloat(e.target.value) || 0)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Event Length (hours)"
                            type="number"
                            value={length}
                            onChange={(e) => setLength(parseFloat(e.target.value) || 0)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Location"
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleSave} color="primary">Save</Button>
            </DialogActions>
        </Dialog>
    );
}

const DefaultEventsTable = withStyles(appStyles)((props) => {
    const {sortieDefaultId, events: initialEvents, firebase, classes} = props;
    const [events, setEvents] = useState(initialEvents || {});
    const [eventFormOpen, setEventFormOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [isAddingEvent, setIsAddingEvent] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const flightOptions = React.useContext(FlightOptionsContext);

    useEffect(() => {
        setEvents(initialEvents || {});
    }, [initialEvents]);

    const handleEdit = (eventId) => {
        setSelectedEvent({id: eventId, ...events[eventId]});
        setIsAddingEvent(false);
        setEventFormOpen(true);
    };

    const handleAdd = () => {
        setSelectedEvent(null);
        setIsAddingEvent(true);
        setEventFormOpen(true);
    };

    const handleDelete = (eventId) => {
        setItemToDelete(eventId);
        setConfirmOpen(true);
    };

    const handleConfirmDelete = () => {
        if (itemToDelete) {
            const newEvents = {...events};
            delete newEvents[itemToDelete];
            firebase.sortieDefault(sortieDefaultId).update({
                [`events.${itemToDelete}`]: fb.firestore.FieldValue.delete()
            }).then(() => {
                setEvents(newEvents);
                setItemToDelete(null);
                setConfirmOpen(false);
            });
        }
    };

    return (
        <>
            <div>
                <Typography variant="h6" gutterBottom component="span">
                    Default Events
                </Typography>
                <Fab size="small" color="secondary" aria-label="add" onClick={handleAdd} className={`${classes.fab} ${classes.fabAdd} ${classes.addEventFab}`}>
                    <Add />
                </Fab>
            </div>
            <Table size="small" aria-label="default events">
                <TableHead>
                    <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell>Time</TableCell>
                        <TableCell>Length</TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {events && Object.keys(events).map((eventId) => {
                        const event = events[eventId];
                        const eventName = flightOptions.sortieEvents[event.type] ? flightOptions.sortieEvents[event.type].name : event.type;
                        return (
                            <TableRow key={eventId} className={classes.hoverRow}>
                                <TableCell>{eventName}</TableCell>
                                <TableCell>{event.time}</TableCell>
                                <TableCell>{event.length}</TableCell>
                                <TableCell>{event.location}</TableCell>
                                <TableCell>
                                    <Box display="flex">
                                        <IconButton aria-label="edit" size="small" onClick={() => handleEdit(eventId)}>
                                            <Edit />
                                        </IconButton>
                                        <IconButton aria-label="delete" size="small" onClick={() => handleDelete(eventId)}>
                                            <Delete />
                                        </IconButton>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
            <DefaultEventForm open={eventFormOpen} handleClose={() => setEventFormOpen(false)} eventData={selectedEvent} sortieDefaultId={sortieDefaultId} firebase={firebase} isAdding={isAddingEvent}/>
            <ConfirmDialog open={confirmOpen}
                           title="Delete Default Event"
                           text="Are you sure you want to delete this default event?"
                           onCancel={() => setConfirmOpen(false)}
                           onConfirm={handleConfirmDelete}/>
        </>
    )
});

const SortieDefaultsSettings = (props) => {
    const {firebase, classes} = props;
    const flightOptions = React.useContext(FlightOptionsContext);
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [isAdding, setIsAdding] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [expandedRows, setExpandedRows] = useState({});

    const handleExpandClick = (id) => {
        setExpandedRows(prev => ({...prev, [id]: !prev[id]}));
    };

    const handleEdit = (id) => {
        setSelected({id, ...flightOptions.sortieDefaults[id]});
        setOpen(true);
    };

    const handleAdd = () => {
        setSelected({
            name: '',
            takeoffTime: 0,
            landTime: 0,
            eShow: 0,
            oShow: 0,
            cr: 0,
            bgColor: '',
            textColor: '',
            events: {},
        });
        setIsAdding(true);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelected(null);
        setIsAdding(false);
    };

    const handleDelete = (id) => {
        if (id === 'XC') return;
        setItemToDelete(id);
        setConfirmOpen(true);
    };

    const handleConfirmDelete = () => {
        if (itemToDelete && itemToDelete !== 'XC') {
            firebase.sortieDefault(itemToDelete).delete().then(() => {
                setItemToDelete(null);
                setConfirmOpen(false);
            });
        }
    };


    if (!flightOptions.sortieDefaults) {
        return null;
    }

    return (
        <Paper className={classes.paper}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Sortie Defaults</Typography>
                <Fab className={`${classes.fab} ${classes.fabAdd}`}
                     color="secondary"
                     aria-label="add"
                     size={"small"}
                     onClick={handleAdd}
                >
                    <Add />
                </Fab>
            </Box>
            <Table className={classes.settingsTable}>
                <TableHead>
                    <TableRow>
                        <TableCell />
                        <TableCell className={classes.settingsTableCell}>Type</TableCell>
                        <TableCell className={classes.settingsTableCell}>Name</TableCell>
                        <TableCell className={classes.settingsTableCell}>Takeoff</TableCell>
                        <TableCell className={classes.settingsTableCell}>Land</TableCell>
                        <TableCell className={classes.settingsTableCell}>E Show</TableCell>
                        <TableCell className={classes.settingsTableCell}>O Show</TableCell>
                        <TableCell className={classes.settingsTableCell}>Crew Ready</TableCell>
                        <TableCell className={classes.settingsTableCell}></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {Object.keys(flightOptions.sortieDefaults).map((keyName, keyIndex) => (
                        <React.Fragment key={keyName}>
                            <TableRow className={classes.hoverRow} style={{
                                backgroundColor: flightOptions.sortieDefaults[keyName].bgColor,
                                color: flightOptions.sortieDefaults[keyName].textColor
                            }}>
                                <TableCell>
                                    <IconButton aria-label="expand row" size="small" onClick={() => handleExpandClick(keyName)}>
                                        {expandedRows[keyName] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                    </IconButton>
                                </TableCell>
                                <TableCell className={classes.settingsTableCellInherit}>{keyName}</TableCell>
                                <TableCell className={classes.settingsTableCellInherit}>{flightOptions.sortieDefaults[keyName].name}</TableCell>
                                <TableCell className={classes.settingsTableCellInherit}>{flightOptions.sortieDefaults[keyName].takeoffTime}</TableCell>
                                <TableCell className={classes.settingsTableCellInherit}>{flightOptions.sortieDefaults[keyName].landTime}</TableCell>
                                <TableCell className={classes.settingsTableCellInherit}>{flightOptions.sortieDefaults[keyName].eShow}</TableCell>
                                <TableCell className={classes.settingsTableCellInherit}>{flightOptions.sortieDefaults[keyName].oShow}</TableCell>
                                <TableCell className={classes.settingsTableCellInherit}>{flightOptions.sortieDefaults[keyName].cr}</TableCell>
                                <TableCell className={classes.settingsTableCellInherit}>
                                    <Box display="flex">
                                        <IconButton aria-label="edit" onClick={() => handleEdit(keyName)}>
                                            <Edit />
                                        </IconButton>
                                        {keyName !== 'XC' && (
                                            <IconButton aria-label="delete" onClick={() => handleDelete(keyName)}>
                                                <Delete />
                                            </IconButton>
                                        )}
                                    </Box>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className={classes.subTableRowCell} colSpan={9}>
                                    <Collapse in={expandedRows[keyName]} timeout="auto" unmountOnExit>
                                        <Box margin={1}>
                                            <DefaultEventsTable sortieDefaultId={keyName} events={flightOptions.sortieDefaults[keyName].events} firebase={firebase} />
                                        </Box>
                                    </Collapse>
                                </TableCell>
                            </TableRow>
                        </React.Fragment>
                    ))}
                </TableBody>
            </Table>
            {selected && <SortieDefaultForm open={open} handleClose={handleClose} data={selected} isAdding={isAdding} firebase={firebase} {...props}/>}
            <ConfirmDialog open={confirmOpen}
                           title="Delete Sortie Default"
                           text="Are you sure you want to delete this sortie default? Removing this sortie type will cause existing sorties with this type to display incorrectly."
                           onCancel={() => setConfirmOpen(false)}
                           onConfirm={handleConfirmDelete}/>
        </Paper>
    );
};

export default withFirebase(withStyles(appStyles)(SortieDefaultsSettings));
